'use client'

import classNames from 'classnames'
import React, { useEffect } from 'react'
import {
  useComponentVisible,
  useLayout,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import Icon from '@dustin-web/microsite-components/src/icon'
import { GetCustomerNavigationQuery } from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import { GET_CUSTOMER_NAVIGATION } from '@dustin-web/microsite-data/src/graphql/content/get-customer-navigation.graphql'
import { DesktopStateActionType, useHeaderContext } from './header-context'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { getNameWithEllipsis } from './header-account-menu'

const CompanyMenu = () => {
  const { dispatchDesktop } = useHeaderContext()

  const { responsive } = useLayout()
  const { translate } = useTranslations()
  const { componentRef, isVisible, setIsVisible } = useComponentVisible<HTMLDivElement>(false)

  useEffect(() => {
    dispatchDesktop({
      type: DesktopStateActionType.SET_MENU_ACTIVE,
      payload: {
        menu: {
          name: 'company',
          active: isVisible,
        },
      },
    })
  }, [isVisible, dispatchDesktop])

  const { data, error } = useGraphql<GetCustomerNavigationQuery>(GET_CUSTOMER_NAVIGATION)

  if (error) {
    throw error
  }

  const customerNavigation = data?.customerNavigation

  if (!customerNavigation) {
    return null
  }

  return !responsive.isSmallScreen ? (
    <li
      className={classNames('c-header__user-item c-header__dropdown', {
        'c-header__dropdown--active': isVisible,
      })}>
      <button
        id="company-btn"
        className="c-header__user-btn c-header__dropdown-btn button-stripped"
        onClick={(e: any) => {
          e.preventDefault()
          return isVisible ? setIsVisible(false) : setIsVisible(true)
        }}
        aria-expanded={isVisible ? true : false}>
        <span className="c-header__user-icon">
          <Icon name="user" className="i-company" />
        </span>
        <span className="c-header__user-title no-mouseflow-dom">
          {customerNavigation.customerName}
        </span>
      </button>
      <div
        className="c-header__dropdown-box c-header__dropdown-box--company u-px-24 u-py-24"
        aria-labelledby="company-btn"
        ref={componentRef}>
        {isVisible && (
          <>
            <h3 className="u-sr-only no-mouseflow-dom">{customerNavigation.customerName}</h3>
            <div className="row">
              <div className="col-4">
                {customerNavigation.parentCustomerName && (
                  <>
                    <h4>
                      {translate('CompanyMenu_YourParentsProductGroups', {
                        parentCustomerName: customerNavigation.parentCustomerName,
                      })}
                    </h4>
                    <ul className="ul-stripped u-mb-16">
                      {customerNavigation.parentCustomerProductCategoryLinks.map((link, key) => (
                        <li key={link.displayName + key}>
                          <a className="a-stripped" href={link.url}>
                            {link.displayName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <h4>{translate('CompanyMenu_YourProductGroups')}</h4>
                <ul className="ul-stripped u-mb-16">
                  {customerNavigation.productCategoryLinks.map((link, key) => (
                    <li key={link.displayName + key}>
                      <a className="a-stripped" href={link.url}>
                        {link.displayName}
                      </a>
                    </li>
                  ))}
                </ul>
                {customerNavigation.documentsAndLinks.length > 0 && (
                  <>
                    <h4>{translate('CompanyMenu_DocumentsAndLinks')}</h4>
                    <ul className="ul-stripped u-mb-16">
                      {customerNavigation.documentsAndLinks.map((link, key) => (
                        <li key={link.displayName + key}>
                          <a className="a-stripped" href={link.url}>
                            {link.displayName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className="col-8 u-pl-48">
                <h4>{translate('CompanyMenu_YourContacts')}</h4>
                <div className="c-header__customer-contacts">
                  {customerNavigation.contacts.map((contact, key) => (
                    <div className="u-mb-24 no-mouseflow-dom" key={contact.name + key}>
                      <h5 className="no-mouseflow-dom">{contact.name}</h5>
                      {contact.title}

                      {contact.phone && (
                        <a
                          className="d-block a-stripped no-mouseflow-dom"
                          href={`tel:${contact.phone ?? ''}`}>
                          {contact.phone ?? ''}
                        </a>
                      )}
                      {contact.email && (
                        <a
                          className="d-block a-stripped no-mouseflow-dom"
                          href={`mailto:${contact.email ?? ''}`}>
                          {contact.email ?? ''}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-12 d-flex align-items-end">
                {customerNavigation.logo && (
                  <img
                    className="c-header__customer-logo u-mr-12"
                    src={customerNavigation.logo.url}
                    style={{ maxHeight: '100px', maxWidth: '200px' }}
                    alt="logo"
                  />
                )}
                <a className="a-stripped" href={customerNavigation.landingPageUrl}>
                  {translate('CompanyMenu_MyStartpage')}
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </li>
  ) : (
    <a
      className="c-btn t-dark-blue u-py-12 u-h-auto u-border-none u-mr-0 u-space-wrap u-text-left u-mb-8 col-12"
      href={customerNavigation.landingPageUrl}>
      <Icon name="office-4" className="i-20 u-mr-8" />
      <span className="u-mr-auto no-mouseflow-dom">
        {getNameWithEllipsis(customerNavigation.customerName, responsive.breakpoints.min.SM)}
      </span>
      <Icon name="arrow-right" className="i-20 d-flex" />
    </a>
  )
}

export default CompanyMenu
